import { style, globalStyle, styleVariants } from '@vanilla-extract/css'
import { vars, focusRing, dot as dotBase } from '../design/theme.css'
import {
  axisTick,
  gloss,
  reading,
  sciName,
  sectionHead,
  sourceMark,
  sourceMarkHover,
  utility,
} from '../design/primitives/text.css'

// The species-detail sheet — a composed reference page in an overlay. Migrated off the
// panel.css/application.css .modal-* shell onto the design system, in three clear zones:
// the fact head (plate · name · counts · description · folklore), the record (the by-date
// run), and the roll of detections. The `#detail-modal` id + `is-open` class are KEPT so the
// legacy refresh controller's "don't refresh while the sheet is open" check still holds.
const NARROW = 'screen and (max-width: 700px)'

export const overlay = style({
  position: 'fixed',
  inset: 0,
  zIndex: vars.z.modal,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: vars.space[6],
  '@media': { [NARROW]: { padding: vars.space[2] } },
  pointerEvents: 'none',
  opacity: 0,
  transition: `opacity ${vars.dur.med} ease`,
  selectors: { '&.is-open': { pointerEvents: 'auto', opacity: 1 } },
})
export const backdrop = style({
  position: 'absolute',
  inset: 0,
  background: vars.color.scrim,
  backdropFilter: 'blur(3px)',
  WebkitBackdropFilter: 'blur(3px)',
})
export const card = style({
  position: 'relative',
  width: '100%',
  maxWidth: '1160px',
  // dvh, not vh: on a phone the browser's own chrome eats the difference, and the sheet's foot
  // (with its close mark) ended up underneath it.
  maxHeight: `calc(100dvh - ${vars.space[7]})`,
  background: vars.color.paper,
  borderRadius: vars.radius.sm,
  boxShadow: vars.shadow.edgeLg,
  padding: `${vars.space[7]} ${vars.space[7]} ${vars.space[6]}`,
  overflow: 'auto',
  '@media': { [NARROW]: { padding: vars.space[5] } },
})

// ---- Head: follow (left) · lang toggle + close (right) ----------------
export const head = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: vars.space[3],
  minHeight: vars.space[5],
  margin: `0 0 ${vars.space[4]}`,
})
export const headRight = style({
  display: 'inline-flex',
  alignItems: 'center',
  gap: vars.space[4],
  marginLeft: 'auto',
})
// EN | GA - the same toggle the masthead carries, parted by a hairline and marked the same way.
const langBase = {
  appearance: 'none',
  background: 'transparent',
  border: 0,
  cursor: 'pointer',
  padding: `${vars.space[1]} ${vars.space[2]}`,
  fontFamily: vars.font.mono,
  fontSize: vars.ms.md,
  letterSpacing: vars.ls.caps,
  lineHeight: vars.lh.snug,
  selectors: { '&:focus-visible': focusRing },
} as const
export const lang = style({ display: 'inline-flex' })
export const langOpt = styleVariants({
  on: {
    ...langBase,
    color: vars.color.ink,
    textDecoration: 'underline',
    textUnderlineOffset: vars.uline.mark,
  },
  off: { ...langBase, color: vars.color.inkSoft },
})
globalStyle(`${lang} button + button`, { borderLeft: `1px solid ${vars.color.rule}` })

// ---- The fact grid: plate | info -------------------------------------
export const grid = style({
  display: 'grid',
  gridTemplateColumns: '0.82fr 1.18fr',
  gap: vars.space[7],
  alignItems: 'start',
  '@media': { [NARROW]: { gridTemplateColumns: '1fr', gap: vars.space[5] } },
})
export const img = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: vars.space[2],
})
globalStyle(`${img} img`, {
  maxWidth: '100%',
  maxHeight: '260px',
  filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.10))',
})
export const info = style({ minWidth: 0 })

// Conservation line — dot + list name + gloss, above the title.
export const consLine = style({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: `${vars.space[1]} ${vars.space[2]}`,
  margin: `0 0 ${vars.space[4]}`,
})
// The same mark, at the same size, in the same voice as the Directory plate's: it was a 9px dot
// beside a 12px bold label here and a 7px dot beside a 10px one there, for one status.
export const consDot = styleVariants({
  red: { ...dotBase, background: vars.color.red },
  amber: { ...dotBase, background: vars.color.amber },
  green: { ...dotBase, background: vars.color.green },
})
export const consName = style([utility, { color: vars.color.ink2 }])
export const consNote = style({
  fontFamily: vars.font.serif,
  fontStyle: 'italic',
  fontSize: vars.fs.sm,
  color: vars.color.inkSoft,
})

// 500, not 700: only the 400 and 500 faces of EB Garamond are bundled, so 700 was a browser
// smearing the 500 into a fake bold, and it read heavier than the same name anywhere else.
export const title = style({
  margin: `${vars.space[1]} ${vars.space[8]} 0 0`,
  fontFamily: vars.font.serif,
  fontWeight: 500,
  fontSize: vars.fs['2xl'],
  lineHeight: vars.lh.tight,
  color: vars.color.ink,
  textWrap: 'balance',
})
// The other-language name under the title - the gloss voice it wears in the Directory, the roll
// and the Stats table, set on a line of its own because this one heads a title block.
// The other-language NAME under the title. It keeps the gloss voice but takes the secondary ink
// rather than the muted one: it sits directly above the binomial, and once the binomial settled
// on one tone across the app the two stacked italic lines were the same grey at two sizes. The
// step now reads title → name → binomial rather than title → two glosses.
export const common = style([
  gloss,
  { display: 'block', margin: `${vars.space[1]} 0 0`, fontSize: vars.fs.md, color: vars.color.ink2 },
])
// The binomial, in the one tone it wears everywhere — it was ink2 here and inkSoft on the
// Directory plate, for the same Latin name on the same bird.
export const sci = style([sciName, { margin: `${vars.space[1]} 0 ${vars.space[3]}` }])

// Three readings, parted by hairline rules and closed with one below — no pills, no plates.
export const stats = style({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  margin: `${vars.space[1]} 0 ${vars.space[4]}`,
  paddingBottom: vars.space[4],
  borderBottom: `1px solid ${vars.color.rule}`,
})
globalStyle(`${stats} > div`, { padding: `0 ${vars.space[4]}` })
globalStyle(`${stats} > div:first-child`, { paddingLeft: 0 })
globalStyle(`${stats} > div + div`, { borderLeft: `1px solid ${vars.color.rule}` })
export const statN = style({
  display: 'block',
  fontFamily: vars.font.serif,
  fontWeight: 500,
  fontSize: vars.fs['2xl'],
  lineHeight: 1,
  color: vars.color.ink,
  fontVariantNumeric: 'tabular-nums',
})
export const statLbl = style([utility, { display: 'block', marginTop: vars.space[2] }])

export const desc = style({
  fontFamily: vars.font.serif,
  fontSize: vars.fs.md,
  lineHeight: vars.lh.relaxed,
  color: vars.color.ink,
  margin: `0 0 ${vars.space[3]}`,
})

// ---- Folklore blocks + the on-demand lookup ---------------------------
// The control is the shared quiet-text button; this only places it and sizes its icon. The
// turning loader is the design system's `spinning`, which stops under prefers-reduced-motion -
// this module's own copy did not.
export const lookup = style({
  display: 'inline-flex',
  alignItems: 'center',
  gap: vars.space[2],
  margin: `${vars.space[1]} 0 ${vars.space[4]}`,
})
globalStyle(`${lookup} .ti`, { fontSize: vars.mark.icon })

// The folklore section — set off from the description/call above by a rule, and each block set off
// from the next by a hairline. The body is verse or story in the serif voice-italic; the TITLE is a
// roman heading so it reads distinctly from the italic body, and the body keeps its own line breaks
// (poems are stored as YAML `|` literals, newlines and all) via pre-line.
export const lore = style({
  marginTop: vars.space[5],
  paddingTop: vars.space[5],
  marginBottom: vars.space[4],
  borderTop: `1px solid ${vars.color.rule}`,
})
export const loreItem = style({
  margin: 0,
  fontFamily: vars.font.serif,
  fontSize: vars.fs.md,
  lineHeight: vars.lh.relaxed,
  color: vars.color.ink,
  fontStyle: 'italic',
  selectors: {
    '& + &': {
      marginTop: vars.space[5],
      paddingTop: vars.space[5],
      borderTop: `1px solid ${vars.color.rule}`,
    },
  },
})
export const loreK = style([utility, { display: 'block', marginBottom: vars.space[1], fontStyle: 'normal' }])
export const loreTitle = style({
  display: 'block',
  margin: `${vars.space[1]} 0 ${vars.space[2]}`,
  fontFamily: vars.font.serif,
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: vars.fs.lg,
  lineHeight: vars.lh.snug,
  color: vars.color.ink,
})
export const loreText = style({ display: 'block', whiteSpace: 'pre-line' })
export const loreVerse = style({
  display: 'block',
  marginTop: vars.space[2],
  whiteSpace: 'pre-line',
  fontStyle: 'italic',
})
export const loreNote = style({
  display: 'block',
  marginTop: vars.space[2],
  fontStyle: 'normal',
  fontSize: vars.fs.sm,
  color: vars.color.inkSoft,
})
export const loreCredit = style({
  display: 'block',
  marginTop: vars.space[2],
  fontFamily: vars.font.mono,
  fontSize: vars.ms.sm,
  fontStyle: 'normal',
  color: vars.color.inkSoft,
})
export const loreSrc = style({
  display: 'inline-flex',
  flexWrap: 'wrap',
  gap: `${vars.space[1]} ${vars.space[3]}`,
  marginLeft: vars.space[2],
  fontFamily: vars.font.mono,
  fontSize: vars.ms.sm,
  fontStyle: 'normal',
  color: vars.color.inkSoft,
})
// LoreCredit's anchors — the source reference and the licence deed — had no rule reaching them and
// so rendered in the browser's default blue, the same bug as the journal's citation tails. Same quiet
// treatment; NO brackets here, because LoreCredit already frames the reference in typographic quotes.
globalStyle(`${loreSrc} a`, {
  ...sourceMark,
})
globalStyle(`${loreSrc} a:hover`, sourceMarkHover)
globalStyle(`${loreSrc} a:focus-visible`, focusRing)

// ---- Call playback ----------------------------------------------------
export const audio = style({ marginTop: vars.space[1] })
export const audioLabel = style([utility, { display: 'block', marginBottom: vars.space[2] }])

// ---- Record section head (shared by history + detections) -------------
export const section = style({
  marginTop: vars.space[6],
  paddingTop: vars.space[4],
  borderTop: `1px solid ${vars.color.rule}`,
})
export const recHead = style({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  marginBottom: vars.space[1],
})
export const recTitle = sectionHead
export const recCount = style([reading, { fontSize: vars.ms.sm, color: vars.color.inkSoft }])

export const historyNote = style({
  margin: `${vars.space[3]} 0 0`,
  fontFamily: vars.font.serif,
  fontSize: vars.fs.sm,
  lineHeight: vars.lh.relaxed,
  color: vars.color.ink2,
})
// The by-date run's date ticks, positioned/shifted inline (data-driven) like the Live line's.
export const anchors = style([
  axisTick,
  { position: 'relative', marginTop: vars.space[1], height: vars.space[4] },
])
globalStyle(`${anchors} span`, { position: 'absolute', top: 0, whiteSpace: 'nowrap' })

// Detections roll — open rows parted by faint dotted rules, no plates.
export const roll = style({ listStyle: 'none', padding: 0, margin: 0, maxHeight: '264px', overflowY: 'auto' })
// Solid, like every other ruled list in the publication - a dotted rule was this one list's own
// idea. The time and the score are readings, so they are set in the mono, not the page serif.
globalStyle(`${roll} li`, {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: vars.space[3],
  padding: `${vars.space[2]} ${vars.space[1]}`,
  borderBottom: `1px solid ${vars.color.rule}`,
})
globalStyle(`${roll} li:last-child`, { borderBottom: 0 })
// A LOCAL class, not an alias: the `small` rule below selects on it, and an alias would reach
// every reading in the app.
export const recWhen = style([reading, {}])
globalStyle(`${recWhen} small`, {
  marginLeft: vars.space[3],
  color: vars.color.inkSoft,
  fontSize: vars.ms.sm,
})
export const recConf = style([reading, { color: vars.color.inkSoft }])
